import { Injectable, Injector, OnDestroy } from '@angular/core';
import { Router, UrlSerializer } from '@angular/router';
import { Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Category } from '~app/modules/generic/models';
import { API_BUYER_CATEGORIES_ROUTE } from '~app/modules/filters/constants';
import { deserialize } from 'serialize-ts/dist';
import { PaginatedResponse } from '~shared/models';
import { CATEGORIES } from '../constants/redis-keys';
import { CacheService } from '~app/core/services/cacheInterface';
import { CACHE_SERVICE } from '~app/core/constants/tokens';
import { HOUR_IN_SECONDS } from '../../../../../server/constants/shared';
import { NUMBER_2 } from '~shared/constants';
import { RegionSelectorService } from '~shared/modules/region-selector/services';
import { queryBuilder } from '@metromarkets/sdk';

@Injectable({
  providedIn: 'root',
})
export class CategoriesService implements OnDestroy {
  destroySubject = new Subject<boolean>();
  private cache: CacheService;

  constructor(
    private http: HttpClient,
    private readonly router: Router,
    private serializer: UrlSerializer,
    injector: Injector,
    private readonly regionSelectorService: RegionSelectorService
  ) {
    this.cache = injector.get(CACHE_SERVICE);
  }

  public getCategories() {
    const url = `${API_BUYER_CATEGORIES_ROUTE()}`;

    return this.getCache(CATEGORIES).pipe(
      switchMap((cacheData) => (cacheData ? of(cacheData) : this.getCategoriesFromUrl(url, CATEGORIES))),
      map((categories) => categories.map((category) => deserialize(category, Category)))
    );
  }

  private setCache(key: string, categories: Category[]) {
    this.cache.set(key, JSON.stringify(categories), HOUR_IN_SECONDS * NUMBER_2);
  }

  private getCache(key: string): Observable<Category[]> {
    return this.cache.get(key).pipe(map((cachedCategories) => JSON.parse(cachedCategories)));
  }

  private getCategoriesFromUrl(url: string, cacheKey: string) {
    const userRegion = this.regionSelectorService.getUserRegion();
    const params = queryBuilder.toParams({
      ...(userRegion && { region: userRegion }),
    }) as HttpParams;

    return this.http.get<PaginatedResponse<Category[]>>(url, { params: params }).pipe(
      map((response) => response.items.map((category) => deserialize(category, Category))),
      tap((categories) => this.setCache(cacheKey, categories))
    );
  }

  public getCategoryByIdOrSlug(idOrSlug: string): Observable<Category> {
    const tree = this.router.createUrlTree([idOrSlug]);

    const userRegion = this.regionSelectorService.getUserRegion();
    const regionParam = userRegion ? `?region=${userRegion}` : '';

    const url = `${API_BUYER_CATEGORIES_ROUTE()}${this.serializer.serialize(tree)}${regionParam}`;

    return this.http.get<Category>(url).pipe(
      map((response) => deserialize(response, Category)),
      catchError((error) => {
        console.error('Error fetching category by ID or Slug:', error);
        return throwError(error);
      })
    );
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
  }
}
